import { selectUserAccountsEnabled } from '@/app/state/app-state/app-state.selectors';
import { State } from '@/app/state/model';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { BTaxLookupService } from '@btax/services/btax-lookup.service';
import { Platform } from '@egovsolutions/angular-platform';
import { selectLookupServiceAvailable } from '@lookup/state/lookup-state/lookup-state.selectors';
import { Store } from '@ngrx/store';
import { NullLists } from '@shared/services/null-lists.service';
import { SimpleErrorHandling } from '@shared/services/simple-error-handling.service';
import { BTaxAccount, BTaxLookupNetworkService, EgovAPIError, PPTAccount, SortInfo } from 'egov-api';
import { BehaviorSubject, combineLatest, of, Subscription } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { selectBTaxStateLoaded } from '../../state/btax-state/btax-state.selectors';
import { selectBTaxAccount } from '../../state/entities/account/btax-account.selectors';

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
    selector: 'btax-results',
    templateUrl: './btax-lookup-results.component.html',
    styleUrls: ['./btax-lookup-results.component.scss'],
    providers:
    [
        BTaxLookupService,
        BTaxLookupNetworkService,
    ],
})
export class BusinessResultsComponent implements OnInit, OnDestroy
{

    constructor
    (
        // Dependencies

        private readonly store: Store<State>,
        private readonly service: BTaxLookupService,
        private readonly simpleErrorHandling: SimpleErrorHandling,
        private readonly nullLists: NullLists,


        // Template dependencies

        protected readonly platform: Platform,
    )
    {
        this.loadResults = this.loadResults.bind(this);
    }


    // Init

    public ngOnInit()
    {
        this.service.requestState();
    }


    // Results

    protected static readonly RESULTS_PER_PAGE = 20;

    protected readonly nullAccounts = this.nullLists.get(BusinessResultsComponent.RESULTS_PER_PAGE);

    protected readonly results$ = new BehaviorSubject<string[] | undefined>(undefined);
    protected readonly loadingResults$ = new BehaviorSubject(false);

    protected showError = false;
    protected showErrorRetry = false;

    @Output() protected readonly resultsUpdated: EventEmitter<void> = new EventEmitter<void>();

    private loadResults(): void
    {
        if (this.currentQuery)
            this.lookup();
        else
        {
            this.results$.next(undefined);
            this.resultsUpdated.emit();
        }
    }

    protected readonly resultAccounts$ = combineLatest([this.results$, this.loadingResults$]).pipe(
        switchMap(([results, loadingResults]) => results
            ? results.length
                ? combineLatest(results.map(accountNumber => this.store.select(selectBTaxAccount(accountNumber)))).pipe(
                    map(accounts => loadingResults
                        ? (accounts as (BTaxAccount | null)[]).concat(this.nullAccounts)
                        : accounts as BTaxAccount[]
                    ),
                    map(accounts => accounts.map(acc => acc ? { ...acc, link: '/business-tax/account/' + acc.accountNumber} : acc))
                )
                : of([])
            : of(this.nullAccounts),
        ),
    );

    protected resultsTrackBy(index: number, account: PPTAccount | null)
    {
        return account?.accountNumber || index;
    }


    // Query

    private currentQuery?: string;
    protected currentSort: SortInfo | null = null;

    private btaxStateSubscription?: Subscription;

    @Input() protected set queryData(data: QueryData)
    {
        if
        (
            data.query !== this.currentQuery
            ||
            data.sort !== this.currentSort?.active
            ||
            data.direction !== this.currentSort?.direction
        )
        {
            this.currentQuery = data.query;

            this.currentSort = data.sort && data.direction
                ? { active: data.sort, direction: data.direction }
                : null;

            this.btaxStateSubscription?.unsubscribe();

            this.btaxStateSubscription = this.store.select(selectLookupServiceAvailable('business-tax')).pipe(
                filter(available => !!available)
            ).subscribe(() => setTimeout(this.loadResults, 0));
        }
    }


    // Load more

    protected loadingMore = false;
    protected ranOutOfResults = false;

    protected get showLoadMore(): boolean { return !this.ranOutOfResults; }
    protected get loadMoreDisabled(): boolean { return this.loadingResults$.value; }


    // Services state

    protected readonly btaxStateLoaded$ = this.store.select(selectBTaxStateLoaded);

    protected readonly userAccountsEnabled$ = this.store.select(selectUserAccountsEnabled);


    // Lookup

    @Output() protected readonly lookupStart: EventEmitter<void> = new EventEmitter<void>();
    @Output() protected readonly lookupError: EventEmitter<void> = new EventEmitter<void>();

    private lookupSubscription?: Subscription;

    private lookup(more: boolean = false)
    {
        if (this.lookupSubscription) this.lookupSubscription.unsubscribe();

        this.showError = false;
        this.lookupStart.emit();
        this.loadingResults$.next(true);

        if (!more)
        {
            this.ranOutOfResults = false;
            this.results$.next(undefined);
        }
        else
            this.loadingMore = true;

        const next = (results: string[]) =>
        {
            if (!more)
                this.results$.next(results);
            else
            {
                this.loadingMore = false;
                this.results$.next(this.results$.value!.concat(results));
            }

            if (results.length < BusinessResultsComponent.RESULTS_PER_PAGE)
                this.ranOutOfResults = true;

            this.loadingResults$.next(false);
            this.resultsUpdated.emit();
        };

        const error = (e: EgovAPIError) =>
        {
            if (!more)
            {
                this.showError = true;
                this.showErrorRetry = !e.permanent;
            }
            else
            {
                this.loadingMore = false;
                this.simpleErrorHandling.displayBestErrorMessage(e, {
                    customMessage: 'Error loading more results.',
                    retryHandler: () => this.lookup(more)
                });
            }

            this.loadingResults$.next(false);
            this.lookupError.emit();
        };

        if (this.currentQuery)
            this.lookupSubscription =
                this.service.lookup(
                    this.currentQuery,
                    this.currentSort || undefined,
                    this.results$.value?.length,
                    BusinessResultsComponent.RESULTS_PER_PAGE
                ).subscribe({ next, error });
    }

    protected retry()
    {
        this.lookup();
    }

    protected loadMore()
    {
        this.lookup(true);
    }


    // Destroy

    public ngOnDestroy()
    {
        this.btaxStateSubscription?.unsubscribe();
    }
}

export interface QueryData
{
    query?: string;
    list?: string;
    sort: string;
    direction: 'asc' | 'desc';
}
